import React from 'react';
import PropTypes from 'prop-types';
import { applyTextStyles, getTokensPropType, selectSystemProps, StackView, Typography, useTheme, useThemeTokens, wrapStringsInText } from '@telus-uds/components-base';
import styled from 'styled-components';
import ItemBase from './ItemBase';
import { htmlAttrs } from '../utils';
import { OL_COUNTER_NAME } from './constants';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([htmlAttrs]);
const selectItemTextStyles = (_ref, themeOptions) => {
  let {
    itemFontWeight,
    itemFontSize,
    itemLineHeight,
    itemFontName,
    itemColor,
    itemTextColor
  } = _ref;
  return applyTextStyles({
    fontWeight: itemFontWeight,
    fontSize: itemFontSize,
    fontName: itemFontName,
    color: itemColor || itemTextColor,
    themeOptions,
    lineHeight: itemLineHeight
  });
};
const StyledItemBase = /*#__PURE__*/styled(ItemBase).withConfig({
  displayName: "Item__StyledItemBase",
  componentId: "components-web__sc-7jzwcq-0"
})(_ref2 => {
  let {
    interItemMargin,
    itemBulletContainerWidth,
    itemBulletTextAlign,
    itemFontWeight,
    itemFontSize,
    itemFontName,
    itemLineHeight,
    themeOptions,
    listGutter,
    itemColor,
    itemTextColor
  } = _ref2;
  return {
    counterIncrement: OL_COUNTER_NAME,
    '::before': {
      content: `counter(${OL_COUNTER_NAME})'.'`,
      display: 'inline-block',
      color: itemColor || itemTextColor,
      width: itemBulletContainerWidth,
      paddingRight: listGutter,
      textAlign: itemBulletTextAlign,
      flexShrink: 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      ...applyTextStyles({
        fontWeight: itemFontWeight,
        fontSize: itemFontSize,
        fontName: itemFontName,
        themeOptions
      }),
      lineHeight: `${itemLineHeight * itemFontSize}px`
    },
    ':not(:last-child)': {
      marginBottom: interItemMargin
    }
  };
});
const ItemContent = /*#__PURE__*/styled.div.withConfig({
  displayName: "Item__ItemContent",
  componentId: "components-web__sc-7jzwcq-1"
})({
  display: 'flex',
  flexDirection: 'column',
  gap: 0
});
const Item = /*#__PURE__*/React.forwardRef((_ref3, ref) => {
  let {
    children,
    counterName,
    title,
    tokens = {},
    variant,
    ...rest
  } = _ref3;
  // We are reusing some tokens from the list component here in order to provide a unified
  // experience
  const themeTokens = useThemeTokens('OrderedList', tokens, variant);
  const headingTokens = title && {
    lineHeight: themeTokens.itemLineHeight,
    fontSize: themeTokens.itemFontSize,
    color: themeTokens.itemColor || themeTokens.itemTextColor,
    fontName: themeTokens.headerFontName,
    fontWeight: themeTokens.headerFontWeight
  };
  const {
    themeOptions
  } = useTheme();
  const itemContent = wrapStringsInText(children, {
    style: selectItemTextStyles(themeTokens, themeOptions)
  });
  return /*#__PURE__*/_jsx(StyledItemBase, {
    ref: ref,
    themeOptions: themeOptions,
    ...themeTokens,
    ...selectProps(rest),
    children: title ? /*#__PURE__*/_jsxs(StackView, {
      tokens: {
        flexShrink: 1
      },
      space: 0,
      children: [/*#__PURE__*/_jsx(Typography, {
        variant: {
          size: 'h4'
        },
        tokens: headingTokens,
        children: title
      }), /*#__PURE__*/_jsx(ItemContent, {
        ...themeTokens,
        children: itemContent
      })]
    }) : /*#__PURE__*/_jsx(ItemContent, {
      children: itemContent
    })
  });
});
Item.displayName = 'OrderedListItem';
Item.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Item content
   */
  children: PropTypes.node.isRequired,
  /**
   * Defines the title of the `OrderedList.Item`
   */
  title: PropTypes.string,
  /**
   * Item tokens
   */
  tokens: getTokensPropType('OrderedList')
};
export default Item;